import { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FiCheckCircle, FiAlertCircle, FiAlertTriangle, FiInfo, FiX } from 'react-icons/fi'
import { useToast } from '../../hooks/useToast'
import { cn } from '../../lib/utils'
import type { ToastType } from '../../hooks/useToast'

const icons: Record<ToastType, React.ReactNode> = {
  success: <FiCheckCircle className="text-green-400" size={18} />,
  error: <FiAlertCircle className="text-red-400" size={18} />,
  warning: <FiAlertTriangle className="text-amber-400" size={18} />,
  info: <FiInfo className="text-brand-primary-light" size={18} />,
}

const borderColors: Record<ToastType, string> = {
  success: 'border-l-green-500',
  error: 'border-l-red-500',
  warning: 'border-l-amber-500',
  info: 'border-l-brand-primary',
}

// 앱 루트에서 단 한 번 렌더링하는 Toast 컨테이너
export default function ToastContainer() {
  const { toasts, subscribe, dismiss } = useToast()

  useEffect(() => {
    const unsub = subscribe()
    return unsub
  }, [subscribe])

  return (
    <div
      aria-live="polite"
      className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-3 pointer-events-none"
    >
      <AnimatePresence>
        {toasts.map((toast) => (
          <motion.div
            key={toast.id}
            initial={{ opacity: 0, x: 60, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 60, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className={cn(
              'pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-md',
              'bg-surface-card border border-surface-elevated/60 border-l-4 shadow-card-dark',
              'max-w-sm w-full',
              borderColors[toast.type],
            )}
          >
            <span className="mt-0.5 shrink-0">{icons[toast.type]}</span>
            <p className="flex-1 text-sm text-ink leading-relaxed">{toast.message}</p>
            <button
              onClick={() => dismiss(toast.id)}
              className="shrink-0 text-ink-faint hover:text-ink transition-colors mt-0.5"
              aria-label="닫기"
            >
              <FiX size={16} />
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}
